import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import type { Session } from '@supabase/supabase-js'
import type { MeExtras, MeView, SsoProvider } from '@takeit4me/shared'
import { api, MOCK } from './api'
import { onUnauthorized, supabase } from './lib/supabase'
import { setCompanyLocale } from './lib/format'
import { onPolicyBlock, type PolicyBlock } from './lib/authPolicy'
import { mfaRoute, needsChallenge } from './lib/mfa'

type Status = 'loading' | 'signed_out' | 'signed_in'
export type Me = MeView & Partial<MeExtras>

interface AuthState {
  status: Status
  session: Session | null
  me: Me | null
  meError: Error | null
  /** The API refused this sign-in for a policy reason (MFA, SSO) or the account is suspended (OP-07, 17.1). */
  block: { code: PolicyBlock; message: string } | null
  /** A verified authenticator exists but this session has not used it yet. */
  mfaChallenge: boolean
  /** The person arrived from a password-recovery link and must choose a new password. */
  recovery: boolean
  signIn(email: string, password: string): Promise<void>
  signInWithProvider(provider: Exclude<SsoProvider, 'saml'>): Promise<void>
  signInWithSaml(domain: string): Promise<void>
  sendMagicLink(email: string): Promise<void>
  sendPasswordReset(email: string): Promise<void>
  setPassword(password: string): Promise<void>
  /** Call after a successful MFA verification: the session is now aal2. */
  mfaDone(): Promise<void>
  signOut(): Promise<void>
  reloadMe(): Promise<void>
}

const Ctx = createContext<AuthState | null>(null)
const origin = () => (typeof location === 'undefined' ? '' : location.origin)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<Status>(MOCK ? 'signed_in' : 'loading')
  const [session, setSession] = useState<Session | null>(null)
  const [me, setMe] = useState<Me | null>(null)
  const [meError, setMeError] = useState<Error | null>(null)
  const [block, setBlock] = useState<AuthState['block']>(null)
  const [mfaChallenge, setMfaChallenge] = useState(false)
  const [recovery, setRecovery] = useState(false)

  const loadMe = useCallback(async () => {
    try {
      const m = await api.me()
      setCompanyLocale(m.company)
      setMe(m); setMeError(null)
      setBlock((b) => (b && b.code !== 'company_suspended' ? null : b))
    } catch (e) {
      setMeError(e instanceof Error ? e : new Error(String(e)))
    }
  }, [])

  useEffect(() => onPolicyBlock((code, message) => {
    // `mfa_required` covers both cases: challenge when a verified authenticator exists, otherwise enrolment.
    if (code === 'mfa_required' && !MOCK) {
      void mfaRoute().then((r) => setBlock({ code: r === 'challenge' ? 'mfa_required' : 'mfa_enrol_required', message }))
      return
    }
    setBlock({ code, message })
  }), [])

  useEffect(() => {
    if (MOCK) { void loadMe(); return }
    const sb = supabase()
    sb.auth.getSession().then(({ data }) => {
      setSession(data.session)
      setStatus(data.session ? 'signed_in' : 'signed_out')
    })
    const { data: sub } = sb.auth.onAuthStateChange((evt, s) => {
      setSession(s)
      setStatus(s ? 'signed_in' : 'signed_out')
      if (evt === 'PASSWORD_RECOVERY') setRecovery(true)
      if (!s) { setMe(null); setBlock(null); setMfaChallenge(false) }
    })
    const off = onUnauthorized(() => { void sb.auth.signOut() })
    return () => { sub.subscription.unsubscribe(); off() }
  }, [loadMe])

  // After any sign-in: does the session still need the second factor? (The API also says so with mfa_required.)
  useEffect(() => {
    if (MOCK || status !== 'signed_in') return
    let live = true
    void needsChallenge().then((n) => { if (live) setMfaChallenge(n) })
    return () => { live = false }
  }, [status, session?.access_token])

  useEffect(() => {
    if (!MOCK && status === 'signed_in' && !me && !mfaChallenge) void loadMe()
  }, [status, me, mfaChallenge, loadMe])

  const signIn = useCallback(async (email: string, password: string) => {
    const { error } = await supabase().auth.signInWithPassword({ email, password })
    if (error) throw new Error(error.message === 'Invalid login credentials' ? 'That email and password do not match.' : error.message)
  }, [])
  const signInWithProvider = useCallback(async (provider: Exclude<SsoProvider, 'saml'>) => {
    const { error } = await supabase().auth.signInWithOAuth({ provider, options: { scopes: 'email', redirectTo: `${origin()}/today` } })
    if (error) throw new Error(error.message)
  }, [])
  const signInWithSaml = useCallback(async (domain: string) => {
    const { data, error } = await supabase().auth.signInWithSSO({ domain, options: { redirectTo: `${origin()}/today` } })
    if (error) throw new Error(/no sso provider/i.test(error.message) ? `Single sign-on is not set up for ${domain} yet. Ask your admin.` : error.message)
    if (data?.url) location.assign(data.url)
  }, [])
  const sendMagicLink = useCallback(async (email: string) => {
    const { error } = await supabase().auth.signInWithOtp({ email, options: { shouldCreateUser: false, emailRedirectTo: `${origin()}/today` } })
    if (error) throw new Error(/signups not allowed|user not found/i.test(error.message) ? 'There is no account for that email. Ask your admin to invite you.' : error.message)
  }, [])
  const sendPasswordReset = useCallback(async (email: string) => {
    const { error } = await supabase().auth.resetPasswordForEmail(email, { redirectTo: `${origin()}/auth/set-password` })
    if (error) throw new Error(error.message)
  }, [])
  const setPassword = useCallback(async (password: string) => {
    const { error } = await supabase().auth.updateUser({ password })
    if (error) throw new Error(error.message)
    setRecovery(false)
  }, [])
  const mfaDone = useCallback(async () => {
    setMfaChallenge(false)
    setBlock(null)
    if (!MOCK) { const { data } = await supabase().auth.refreshSession(); setSession(data.session) }
    await loadMe()
  }, [loadMe])

  const signOut = useCallback(async () => {
    if (MOCK) return
    await supabase().auth.signOut()
    setMe(null); setBlock(null)
  }, [])

  const value = useMemo(() => ({
    status, session, me, meError, block, mfaChallenge, recovery,
    signIn, signInWithProvider, signInWithSaml, sendMagicLink, sendPasswordReset, setPassword, mfaDone, signOut, reloadMe: loadMe,
  }), [status, session, me, meError, block, mfaChallenge, recovery, signIn, signInWithProvider, signInWithSaml, sendMagicLink, sendPasswordReset, setPassword, mfaDone, signOut, loadMe])
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

export function useAuth() {
  const v = useContext(Ctx)
  if (!v) throw new Error('useAuth outside AuthProvider')
  return v
}

/** The signed-in staff member and company. Only use under the authenticated layout. */
export function useMe(): Me {
  const { me } = useAuth()
  if (!me) throw new Error('useMe before /me loaded')
  return me
}
